import { Badge, Box, Button, Flex, HStack, Text } from '@chakra-ui/react'
import {
  ContactType,
  Encounter,
  EncounterStatus,
  EncounterType,
  FollowUpAction,
  getDateString,
  PatientStatement,
  PrenatalExamData,
  ProviderFlag,
  TelephoneContact,
  UltrasoundData,
  UltrasoundScenario,
  UltrasoundStudyType,
  WithId,
} from '@hb/shared'
import type { FlatThreadMessage } from '../../../../hooks/backend'
import { formatGa, getEncounterPresentingComplaint } from './utils'

const statusColor: Record<string, string> = {
  [EncounterStatus.DRAFT]: 'yellow',
  [EncounterStatus.COMPLETED]: 'blue',
  [EncounterStatus.AUTHENTICATED]: 'green',
}

const contactTypeLabel: Record<ContactType, string> = {
  'phone-call': 'Phone Call',
  'text-message': 'Text Message',
  'patient-portal-message': 'Portal Message',
  'in-person-without-visit': 'In-Person',
  'after-hours-contact': 'After-Hours',
}

const followUpLabel: Record<FollowUpAction, string> = {
  none: 'No follow-up',
  'visit-scheduled': 'Visit Scheduled',
  prescription: 'Prescription',
  referral: 'Referral',
  'er-advised': 'ER Advised',
  other: 'Other',
}

const followUpColor: Record<FollowUpAction, string> = {
  none: 'gray',
  'visit-scheduled': 'blue',
  prescription: 'green',
  referral: 'purple',
  'er-advised': 'red',
  other: 'orange',
}

const scenarioLabel: Record<UltrasoundScenario, string> = {
  'provider-pocus': 'Provider POCUS',
  'sonographer-study': 'Sonographer Study',
}

const studyTypeLabel: Record<UltrasoundStudyType, string> = {
  'limited-pocus': 'Limited POCUS',
  'standard-ob': 'Standard OB',
  detailed: 'Detailed',
  bpp: 'BPP',
  'nst-us': 'NST+US',
  transvaginal: 'Transvaginal',
}

const EDIT_WINDOW_MS = 30 * 60 * 1000

const VisitCard = ({
  encounter,
  onOpen,
}: {
  encounter: WithId<Encounter>
  onOpen: (id: string) => void
}) => {
  const data = encounter.data as PrenatalExamData
  const complaint = getEncounterPresentingComplaint(encounter)

  return (
    <Box
      bg="white"
      _dark={{ bg: 'gray.800' }}
      borderRadius={8}
      boxShadow="0 1px 4px #00000018"
      border="1px solid"
      borderColor="gray.200"
      p={4}
      cursor="pointer"
      onClick={() => onOpen(encounter.id)}
      _hover={{ borderColor: 'blue.300', boxShadow: '0 2px 8px #00000028' }}
      transition="all 0.15s">
      <Flex align="center" gap={3} flexWrap="wrap">
        <Text fontFamily="Hero-New" fontWeight={700} fontSize="sm">
          {encounter.date ? getDateString(encounter.date, 'short', true) : '—'}
        </Text>
        {data.gestationalAge != null && (
          <Badge colorScheme="blue" fontFamily="Hero-New" fontSize="xs">
            {formatGa(data.gestationalAge)}
          </Badge>
        )}
        {encounter.renderingProviderInitials && (
          <Badge colorScheme="gray" fontFamily="Hero-New" fontSize="xs">
            {encounter.renderingProviderInitials}
          </Badge>
        )}
        <Badge
          colorScheme={statusColor[encounter.status] ?? 'gray'}
          fontFamily="Hero-New"
          fontSize="xs">
          {encounter.status}
        </Badge>
      </Flex>
      {complaint && (
        <Text
          mt={2}
          fontSize="sm"
          color="gray.600"
          _dark={{ color: 'gray.300' }}
          fontFamily="Hero-New">
          {complaint}
        </Text>
      )}
      {encounter.type === EncounterType.ANTEPARTUM && encounter.data?.quickNoteOrigin && (
        <Badge mt={2} colorScheme="orange" fontFamily="Hero-New" fontSize="xs">
          Quick Note
        </Badge>
      )}
    </Box>
  )
}

const ContactBar = ({
  contact,
  onEdit,
}: {
  contact: WithId<TelephoneContact>
  onEdit?: (contact: WithId<TelephoneContact>) => void
}) => {
  const dateStr = contact.date ? getDateString(contact.date.slice(0, 10), 'short', true) : '—'
  const summary =
    contact.clinicalSummary.length > 80
      ? `${contact.clinicalSummary.slice(0, 80)}…`
      : contact.clinicalSummary

  // Evaluate at render time — server enforces the actual lock
  const isEditable =
    !!onEdit &&
    !!contact.createdAt &&
    Date.now() - new Date(contact.createdAt).getTime() < EDIT_WINDOW_MS

  return (
    <Box
      bg="gray.50"
      _dark={{ bg: 'gray.700', borderColor: 'whiteAlpha.200' }}
      borderRadius={6}
      border="1px solid"
      borderColor="gray.200"
      px={3}
      py={2}>
      <Flex align="center" gap={2} flexWrap="wrap">
        <Badge colorScheme="teal" fontFamily="Hero-New" fontSize="xs">
          {contactTypeLabel[contact.contactType] ?? contact.contactType}
        </Badge>
        <Text fontFamily="Hero-New" fontSize="xs" color="gray.500">
          {dateStr}
          {contact.durationMinutes ? ` · ${contact.durationMinutes} min` : ''}
        </Text>
        {contact.followUpAction !== 'none' && (
          <Badge
            colorScheme={followUpColor[contact.followUpAction] ?? 'gray'}
            fontFamily="Hero-New"
            fontSize="xs">
            {followUpLabel[contact.followUpAction] ?? contact.followUpAction}
          </Badge>
        )}
        {contact.billingStatus === 'review-for-billing' && (
          <Badge colorScheme="blue" fontFamily="Hero-New" fontSize="xs">
            Review for billing
          </Badge>
        )}
        {isEditable && (
          <Button
            size="xs"
            variant="ghost"
            colorScheme="gray"
            fontFamily="Hero-New"
            ml="auto"
            onClick={e => {
              e.stopPropagation()
              onEdit(contact)
            }}>
            Edit
          </Button>
        )}
      </Flex>
      {summary && (
        <Text
          mt={1}
          fontSize="xs"
          color="gray.600"
          _dark={{ color: 'gray.400' }}
          fontFamily="Hero-New">
          {summary}
        </Text>
      )}
    </Box>
  )
}

const UltrasoundCard = ({
  encounter,
  onOpen,
}: {
  encounter: WithId<Encounter>
  onOpen: (id: string) => void
}) => {
  const data = encounter.data as UltrasoundData

  return (
    <Box
      bg="teal.50"
      _dark={{ bg: 'teal.900', borderColor: 'teal.600' }}
      borderRadius={6}
      border="1px solid"
      borderColor="teal.200"
      px={3}
      py={2}
      cursor="pointer"
      onClick={() => onOpen(encounter.id)}
      _hover={{ borderColor: 'teal.400', boxShadow: '0 2px 8px #00000028' }}
      transition="all 0.15s">
      <Flex align="center" gap={2} flexWrap="wrap">
        <Text
          fontFamily="Hero-New"
          fontWeight={700}
          fontSize="xs"
          color="teal.700"
          _dark={{ color: 'teal.200' }}>
          {encounter.date ? getDateString(encounter.date, 'short', true) : '—'}
        </Text>
        <Badge colorScheme="teal" fontFamily="Hero-New" fontSize="xs">
          Ultrasound
        </Badge>
        {data.scenarioType && (
          <Badge colorScheme="teal" variant="subtle" fontFamily="Hero-New" fontSize="xs">
            {scenarioLabel[data.scenarioType] ?? data.scenarioType}
          </Badge>
        )}
        {data.studyType && (
          <Badge colorScheme="cyan" fontFamily="Hero-New" fontSize="xs">
            {studyTypeLabel[data.studyType] ?? data.studyType}
          </Badge>
        )}
        {data.gaAtStudyDays != null && (
          <Badge colorScheme="blue" variant="subtle" fontFamily="Hero-New" fontSize="xs">
            {formatGa(data.gaAtStudyDays)}
          </Badge>
        )}
        <Badge
          colorScheme={statusColor[encounter.status] ?? 'gray'}
          fontFamily="Hero-New"
          fontSize="xs">
          {encounter.status}
        </Badge>
      </Flex>
    </Box>
  )
}

const FlagBar = ({ flag }: { flag: WithId<ProviderFlag> }) => (
  <Box
    w="100%"
    px={3}
    py={1}
    borderLeftWidth="3px"
    borderColor={flag.removedAt ? 'gray.300' : flag.source === 'system' ? 'red.400' : 'orange.400'}
    bg={flag.removedAt ? 'gray.50' : flag.source === 'system' ? 'red.50' : 'orange.50'}
    _dark={{
      bg: flag.removedAt ? 'gray.700' : flag.source === 'system' ? 'red.900' : 'orange.900',
    }}>
    <HStack spacing={2}>
      <Badge colorScheme={flag.source === 'system' ? 'red' : 'orange'} fontSize="2xs">
        {flag.source === 'system' ? 'System Flag' : 'Provider Flag'}
      </Badge>
      <Text fontSize="xs" color={flag.removedAt ? 'gray.400' : 'gray.700'} fontFamily="Hero-New">
        {flag.text}
      </Text>
      {flag.removedAt && (
        <Text fontSize="2xs" color="gray.400" fontFamily="Hero-New">
          resolved
        </Text>
      )}
    </HStack>
  </Box>
)

const PortalMessageBar = ({ message }: { message: FlatThreadMessage }) => {
  const dateStr = new Date(message.createdOn).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
  const preview = message.text.length > 80 ? `${message.text.slice(0, 80)}…` : message.text

  return (
    <Box
      w="100%"
      px={3}
      py={1.5}
      borderLeftWidth="3px"
      borderColor="blue.300"
      bg="blue.50"
      _dark={{ bg: 'blue.900', borderColor: 'blue.600' }}>
      <HStack spacing={2}>
        <Badge colorScheme="blue" fontSize="2xs" fontFamily="Hero-New">
          Portal Message
        </Badge>
        <Text fontSize="xs" color="gray.500" fontFamily="Hero-New">
          {dateStr}
        </Text>
      </HStack>
      {preview && (
        <Text
          fontSize="xs"
          color="gray.600"
          _dark={{ color: 'gray.400' }}
          fontFamily="Hero-New"
          mt={0.5}>
          {preview}
        </Text>
      )}
    </Box>
  )
}

const PatientStatementBar = ({ statement }: { statement: WithId<PatientStatement> }) => {
  const dateStr = new Date(statement.submittedAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  const preview =
    statement.statementText.length > 80
      ? `${statement.statementText.slice(0, 80)}…`
      : statement.statementText

  return (
    <Box
      w="100%"
      px={3}
      py={1.5}
      borderLeftWidth="3px"
      borderColor="orange.400"
      bg="orange.50"
      _dark={{ bg: 'orange.900', borderColor: 'orange.500' }}>
      <HStack spacing={2}>
        <Badge colorScheme="orange" fontSize="2xs" fontFamily="Hero-New">
          Patient Statement
        </Badge>
        <Text fontSize="xs" color="gray.500" fontFamily="Hero-New">
          {dateStr}
        </Text>
      </HStack>
      {preview && (
        <Text
          fontSize="xs"
          color="gray.600"
          _dark={{ color: 'gray.400' }}
          fontFamily="Hero-New"
          mt={0.5}>
          {preview}
        </Text>
      )}
    </Box>
  )
}

type TimelineItem =
  | { kind: 'encounter'; item: WithId<Encounter>; sortKey: string }
  | { kind: 'contact'; item: WithId<TelephoneContact>; sortKey: string }
  | { kind: 'ultrasound'; item: WithId<Encounter>; sortKey: string }
  | { kind: 'flag'; item: WithId<ProviderFlag>; sortKey: string }
  | { kind: 'portal-message'; item: FlatThreadMessage; sortKey: string }
  | { kind: 'patient-statement'; item: WithId<PatientStatement>; sortKey: string }

interface Props {
  encounters: WithId<Encounter>[] // expected ascending order
  contacts: WithId<TelephoneContact>[] // may be any order
  ultrasoundEncounters: WithId<Encounter>[] // may be any order
  flags?: WithId<ProviderFlag>[] // provider and system flags
  portalMessages?: FlatThreadMessage[] // patient thread messages scoped to episode
  patientStatements?: WithId<PatientStatement>[] // patient written statements for the episode
  onOpenEncounter: (id: string) => void
  onEditContact?: (contact: WithId<TelephoneContact>) => void
}

export const FlowSheetTimeline = ({
  encounters,
  contacts,
  ultrasoundEncounters,
  flags,
  portalMessages,
  patientStatements,
  onOpenEncounter,
  onEditContact,
}: Props) => {
  // Merge all sources into a single sorted ascending list
  const items: TimelineItem[] = [
    ...encounters.map(
      enc =>
        ({
          kind: 'encounter',
          item: enc,
          sortKey: enc.date ?? '',
        }) as TimelineItem,
    ),
    ...contacts.map(
      c =>
        ({
          kind: 'contact',
          item: c,
          sortKey: c.date ?? '',
        }) as TimelineItem,
    ),
    ...ultrasoundEncounters.map(
      enc =>
        ({
          kind: 'ultrasound',
          item: enc,
          sortKey: enc.date ?? '',
        }) as TimelineItem,
    ),
    ...(flags ?? []).map(
      f =>
        ({
          kind: 'flag',
          item: f,
          sortKey: f.addedAt ?? '',
        }) as TimelineItem,
    ),
    ...(portalMessages ?? []).map(
      m =>
        ({
          kind: 'portal-message',
          item: m,
          // ISO timestamp gives sub-day ordering consistent with other items
          sortKey: new Date(m.createdOn).toISOString(),
        }) as TimelineItem,
    ),
    ...(patientStatements ?? []).map(
      s =>
        ({
          kind: 'patient-statement',
          item: s,
          sortKey: new Date(s.submittedAt).toISOString(),
        }) as TimelineItem,
    ),
  ].sort((a, b) => a.sortKey.localeCompare(b.sortKey))

  if (items.length === 0) {
    return (
      <Box p={6} textAlign="center">
        <Text color="gray.400" fontFamily="Hero-New" fontSize="sm">
          No antepartum visits or contacts recorded yet.
        </Text>
      </Box>
    )
  }

  return (
    <Box px={4} py={3}>
      <Flex direction="column" gap={2}>
        {items.map(item =>
          item.kind === 'encounter' ? (
            <VisitCard key={`enc-${item.item.id}`} encounter={item.item} onOpen={onOpenEncounter} />
          ) : item.kind === 'ultrasound' ? (
            <UltrasoundCard
              key={`us-${item.item.id}`}
              encounter={item.item}
              onOpen={onOpenEncounter}
            />
          ) : item.kind === 'flag' ? (
            <FlagBar key={`flag-${item.item.id}`} flag={item.item} />
          ) : item.kind === 'portal-message' ? (
            <PortalMessageBar key={`pm-${item.item.id}`} message={item.item} />
          ) : item.kind === 'patient-statement' ? (
            <PatientStatementBar key={`ps-${item.item.id}`} statement={item.item} />
          ) : (
            <ContactBar
              key={`contact-${item.item.id}`}
              contact={item.item}
              onEdit={onEditContact}
            />
          ),
        )}
      </Flex>
    </Box>
  )
}
